import React, { useState, useEffect } from 'react';
import blockchainService from '../../services/blockchainService';
import {
  Wallet,
  Lock,
  Unlock,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  Download,
  Upload,
  Eye,
  Edit,
  Trash2,
  Plus,
  X,
  Save
} from 'lucide-react';

interface WalletInfo {
  id: string;
  address: string;
  nickname?: string;
  balance: {
    vttn: number;
    usdt: number;
  };
  status: 'active' | 'frozen' | 'suspended';
  tier: 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM' | 'DIAMOND';
  kycStatus: 'PENDING' | 'VERIFIED' | 'REJECTED';
  lastActivity: string;
  frozenReason?: string;
  frozenAt?: string;
}

const WalletManagementPage: React.FC = () => {
  const [wallets, setWallets] = useState<WalletInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'frozen' | 'suspended'>('all');
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<WalletInfo | null>(null);
  const [freezeReason, setFreezeReason] = useState('');

  useEffect(() => {
    loadWallets();
  }, []);

  const loadWallets = async () => {
    setLoading(true);
    try {
      // Mock 데이터 - 실제로는 API에서 가져옴
      const mockWallets: WalletInfo[] = [
        {
          id: '1',
          address: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2',
          nickname: 'Alice Wallet',
          balance: { vttn: 15000, usdt: 5000 },
          status: 'active',
          tier: 'GOLD',
          kycStatus: 'VERIFIED',
          lastActivity: '2024-01-15T10:30:00Z'
        },
        {
          id: '2',
          address: '0x8ba1f109551bD432803012645Hac136c22C177',
          nickname: 'Bob Wallet',
          balance: { vttn: 8500, usdt: 2000 },
          status: 'frozen',
          tier: 'SILVER',
          kycStatus: 'VERIFIED',
          lastActivity: '2024-01-14T15:45:00Z',
          frozenReason: '의심스러운 거래 활동',
          frozenAt: '2024-01-14T16:00:00Z'
        },
        {
          id: '3',
          address: '0x1234567890123456789012345678901234567890',
          nickname: 'Charlie Wallet',
          balance: { vttn: 25000, usdt: 10000 },
          status: 'active',
          tier: 'PLATINUM',
          kycStatus: 'VERIFIED',
          lastActivity: '2024-01-15T09:15:00Z'
        }
      ];
      setWallets(mockWallets);
    } catch (error) {
      console.error('Failed to load wallets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFreezeWallet = async () => {
    if (!selectedWallet || !freezeReason.trim()) {
      alert('지갑과 동결 사유를 모두 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      
      const result = await blockchainService.freezeWallet(
        'voltron',
        selectedWallet.address,
        freezeReason
      );

      if (result.success) {
        alert(`지갑이 성공적으로 동결되었습니다.\n지갑 주소: ${result.walletAddress}\n사유: ${result.reason}\n트랜잭션 해시: ${result.txHash}`);
        
        // 지갑 목록 업데이트
        setWallets(prev => prev.map(wallet => 
          wallet.id === selectedWallet.id 
            ? { ...wallet, status: 'frozen' as const, frozenReason: freezeReason, frozenAt: result.frozenAt }
            : wallet
        ));
      }

      setShowFreezeModal(false);
      setSelectedWallet(null);
      setFreezeReason('');
    } catch (error) {
      console.error('Wallet freeze failed:', error);
      alert(`지갑 동결 중 오류가 발생했습니다: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUnfreezeWallet = async (wallet: WalletInfo) => {
    try {
      setLoading(true);
      
      const result = await blockchainService.unfreezeWallet(
        'voltron',
        wallet.address
      );

      if (result.success) {
        alert(`지갑 동결이 해제되었습니다.\n지갑 주소: ${result.walletAddress}\n트랜잭션 해시: ${result.txHash}`);
        
        // 지갑 목록 업데이트
        setWallets(prev => prev.map(w => 
          w.id === wallet.id 
            ? { ...w, status: 'active' as const, frozenReason: undefined, frozenAt: undefined }
            : w
        ));
      }
    } catch (error) {
      console.error('Wallet unfreeze failed:', error);
      alert(`지갑 동결 해제 중 오류가 발생했습니다: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredWallets = wallets.filter(wallet => {
    const matchesSearch = wallet.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (wallet.nickname && wallet.nickname.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterStatus === 'all' || wallet.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'frozen':
        return <Lock className="w-4 h-4 text-red-500" />;
      case 'suspended':
        return <XCircle className="w-4 h-4 text-orange-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'frozen':
        return 'bg-red-100 text-red-800';
      case 'suspended':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">지갑 관리</h1>
          <p className="text-gray-600">Voltron 네트워크 지갑 출금 제어 및 관리</p>
        </div>
        <div className="flex space-x-3">
          <button className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
            <Download className="w-4 h-4" />
            <span>내보내기</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
            <Upload className="w-4 h-4" />
            <span>가져오기</span>
          </button>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="지갑 주소 또는 닉네임 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 w-64"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">모든 상태</option>
              <option value="active">활성</option>
              <option value="frozen">동결</option>
              <option value="suspended">정지</option>
            </select>
          </div>
          <div className="text-sm text-gray-600">
            총 {filteredWallets.length}개 지갑
          </div>
        </div>
      </div>

      {/* 지갑 목록 */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">지갑 정보</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">잔액</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">등급</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">마지막 활동</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">작업</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredWallets.map((wallet) => (
                <tr key={wallet.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {wallet.nickname || '익명 지갑'}
                      </div>
                      <div className="text-sm text-gray-500 font-mono">
                        {wallet.address.slice(0, 10)}...{wallet.address.slice(-8)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {wallet.balance.vttn.toLocaleString()} VTTN
                    </div>
                    <div className="text-sm text-gray-500">
                      {wallet.balance.usdt.toLocaleString()} USDT
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      wallet.tier === 'DIAMOND' ? 'bg-purple-100 text-purple-800' :
                      wallet.tier === 'PLATINUM' ? 'bg-gray-100 text-gray-800' :
                      wallet.tier === 'GOLD' ? 'bg-yellow-100 text-yellow-800' :
                      wallet.tier === 'SILVER' ? 'bg-gray-100 text-gray-600' :
                      'bg-orange-100 text-orange-800'
                    }`}>
                      {wallet.tier}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(wallet.status)}
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(wallet.status)}`}>
                        {wallet.status === 'active' ? '활성' : wallet.status === 'frozen' ? '동결' : '정지'}
                      </span>
                    </div>
                    {wallet.frozenReason && (
                      <div className="text-xs text-red-600 mt-1">
                        {wallet.frozenReason}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(wallet.lastActivity).toLocaleDateString('ko-KR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      {wallet.status === 'active' ? (
                        <button
                          onClick={() => {
                            setSelectedWallet(wallet);
                            setShowFreezeModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Lock className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUnfreezeWallet(wallet)}
                          className="text-green-600 hover:text-green-900"
                        >
                          <Unlock className="w-4 h-4" />
                        </button>
                      )}
                      <button className="text-blue-600 hover:text-blue-900">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 지갑 동결 모달 */}
      {showFreezeModal && selectedWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">지갑 동결</h3>
              <button
                onClick={() => {
                  setShowFreezeModal(false);
                  setSelectedWallet(null);
                  setFreezeReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">지갑 주소</label>
                <div className="text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">
                  {selectedWallet.address}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">동결 사유</label>
                <textarea
                  value={freezeReason}
                  onChange={(e) => setFreezeReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  rows={3}
                  placeholder="동결 사유를 입력하세요..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowFreezeModal(false);
                  setSelectedWallet(null);
                  setFreezeReason('');
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                취소
              </button>
              <button
                onClick={handleFreezeWallet}
                disabled={loading || !freezeReason.trim()}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {loading ? '처리 중...' : '동결 실행'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletManagementPage;